import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import ContainerPainel from 'components/container_painel';
import { createPermission, getModulesList, getPermission, updatePermission } from 'api/permission/manage-pemission.api';
import { useEffect, useState } from 'react';
import UserFormSteps from 'components/user/user_form_steps';
import PermissionDataForm from 'components/permission/permission_data_form';
import { ListDropdown } from 'api/common.types';
import { getGroupsDropdown } from 'api/group/group.api';
import { Module, PermissionFormData } from 'types/permission';
import PermissionView from 'components/permission/permission_data_view';
import { convertPermissionData } from 'utils/conversor';
import { getProfilesDropdown } from 'api/profile/profile.api';
import { useAuth } from 'contexts/auth/auth_context';

enum PermissionFormAction {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

const administratorsList: string[]= ['ADMINISTRADOR'];
const PermissionFormPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();


  const { user } = useAuth();
  const isAdministrator = administratorsList.includes(user!.permission.name);

  useEffect(() => {
    if (!isAdministrator) {
      navigate(ROUTES.HOME);
    }
  }, [isAdministrator, navigate]);

  const pathParts = location.pathname.split('/');
  const permissionUuid = pathParts[3];
  let action = 'create';

  if(permissionUuid){
    action = 'edit';
  }

  const [activeStep, setActiveStep] = useState(1);
  const [permissionFormAction, setPermissionFormAction] = useState<PermissionFormAction | null>(null);
  const [permissionDetails, setPermissionDetails] = useState<PermissionFormData | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [groupsDropdown, setGroupsDropdown] = useState<ListDropdown[]>([]);
  const [profilesGroup, setProfilesGroup] = useState<ListDropdown[]>([]);


  useEffect(() => {
    const fetchModules = async () => {
      const result = await getModulesList({});
      setModules((result.data.filter( (module: Module) => module.name !== 'OTHERS')));
    };

    const fetchDropdowns = async () => {
      const result = await getGroupsDropdown();
      setGroupsDropdown(result.data);
    };

    const fetchProfilesSubgroup = async () => {
      const result =await getProfilesDropdown();
      setProfilesGroup(result.data);
    };

    fetchDropdowns();
    fetchModules();
    fetchProfilesSubgroup();
  }, []);

  const onGroupChange = async (groupUuid: string) => {
    const result = await getProfilesDropdown(groupUuid);
    setProfilesGroup(result.data);
  }

  useEffect(() => {
    const fetchPermissionDetails = async (uuid: string) => {
      try {
        const permission: PermissionFormData = convertPermissionData((await getPermission(uuid)));
        setPermissionDetails(permission);
      } catch (error) {
        console.error('Error fetching permission details:', error);
      }
    };

    if (action === 'create') {
      setPermissionFormAction(PermissionFormAction.CREATE);
    } else if (action === 'edit' && permissionUuid) {
      setPermissionFormAction(PermissionFormAction.EDIT);
      fetchPermissionDetails(permissionUuid);
    }
  }, [action, permissionUuid]);

  const initialValues: PermissionFormData = {
    name: permissionDetails?.name ?? '',
    groupUuid: permissionDetails?.groupUuid ?? '',
    profileUuid: permissionDetails?.profileUuid ?? '',
    permissions: permissionDetails?.permissions ?? { },
  };

  const validationSchema = Yup.object().shape({
    profileUuid: Yup.string().required('O Subgrupo é obrigatório'),
    groupUuid: Yup.string().required('O grupo é obrigatório'),
    name: Yup.string().required('Nome do perfil é obrigatório'),
  });

  const handleSubmit = async (
    values: PermissionFormData,
    actions: FormikHelpers<PermissionFormData>,
  ) => {
    if (activeStep === 1) {
      setActiveStep(2);
      actions.setSubmitting(false);
      return;
    }

    try {
        if (permissionFormAction === PermissionFormAction.CREATE) {
          try{
            await createPermission({
              ...values,
              name: values.name.toUpperCase()
            });
            navigate(ROUTES.MANAGE_PERMISSIONS);
          }catch{
            alert(`Perfil já existente. Mude o nome e tente novamente`);
          }
        }

        if (permissionFormAction === PermissionFormAction.EDIT && permissionUuid) {
          try{
            const modulePermissions = Object.keys(values.permissions || {}).length
            ? values.permissions
            : permissionDetails?.permissions

            await updatePermission(permissionUuid, {
              ...values,
              name: values.name.toUpperCase(),
              permissions: modulePermissions
            });
           navigate(ROUTES.MANAGE_PERMISSIONS);
          }catch{
            alert(`Perfil já existente. Mude o nome e tente novamente`);
          }
        }
      } catch (error) {
        console.log()
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (activeStep === 2) {
      setActiveStep(1);
    } else {
      navigate(ROUTES.MANAGE_PERMISSIONS);
    }
  };

  return (
    <ContainerPainel
      pagina={
        permissionFormAction === PermissionFormAction.CREATE
          ? 'Criar Perfil'
          : 'Editar Perfil'
      }
      showBackButton={true}
    >
      <Box
        sx={{
          backgroundColor: '#FAFAFA',
        }}
      >
        <UserFormSteps activeStep={activeStep} />
        <Box
          sx={{
            height: '79px',
            gap: '24px',
          }}
        >
          <Typography sx={{ textAlign: 'center' }} variant='h5'>
            {activeStep === 1 ? 'Dados principais' : 'Confirmação dos dados'}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '22.88px',
              letterSpacing: '0.17px',
              textAlign: 'center',
              marginTop: '20px',
            }}
          >
            {activeStep === 1
              ? 'Informe corretamente os dados principais do perfil de usuário cadastrado.'
              : 'Confira todas as informações que foram preenchidas, e altere-as caso necessário.'}
          </Typography>
        </Box>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          context={{ action }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            dirty,
          }) => {

            return (
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'nowrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {activeStep === 1 ? (
                      <PermissionDataForm
                        setFieldValue={setFieldValue}
                        values={values}
                        errors={errors}
                        touched={touched}
                        modules={modules}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        groupsOption={groupsDropdown}
                        clearDuplicateError={() => {}}
                        duplicateError={null}
                        onGroupChange={onGroupChange}
                        profilesGroup={profilesGroup}
                      />
                  ) : (
                        <PermissionView
                          values={values}
                          groupList={groupsDropdown}
                          profileSubgroupList={profilesGroup}
                          modules={modules}
                        />
                  )}
                </Box>

                <Box sx={{ justifySelf: 'center', padding: '32px' }}>
                  <Button
                    variant='text'
                    onClick={handleBack}
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      letterSpacing: '0.4px',
                      textAlign: 'left',
                      color: '#0048B6',
                      marginRight: '20px',
                    }}
                  >
                    VOLTAR
                  </Button>
                  <Button
                    variant='contained'
                    type="submit"
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      letterSpacing: '0.4px',
                      textAlign: 'left',
                      background: '#0048B6',
                    }}
                  >
                    {activeStep === 1 ? 'AVANÇAR' : 'CONFIRMAR E SALVAR'}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    </ContainerPainel>
  );
};

export default PermissionFormPage;
